import PropTypes from 'prop-types';
import React, { useEffect, useRef, useCallback } from 'react';
import { compose } from 'redux';
import pipe from 'lodash/fp/pipe';
import { findPropertiesByScope } from '@pearsontechnology/aero-plugin-schema';

import { getPluginSchema } from 'data/Plugin/actions';
import { STAGE_ELEMENT_TYPES } from 'pluginServices/editorState/constants';
import { STAGE } from './helpers/constants';
import {
  computeEditorSelection,
  extractUrnFromString,
  getClip,
  registerNewElement,
  generateNewDataIds,
  reduceInteractiveConfig,
  setClip,
  serializeOrUpdateRefsInPluginConfigs,
  identifyAndRegisterImagesOfElement,
} from 'containers/Clipboard/helpers';
import { isNotPlugin } from 'containers/Clipboard/helpers/predicates';
import { performSideEffectWithValueAndReturnOriginal } from 'containers/Clipboard/helpers/utils';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { pushToast } from 'containers/ToastManager/actions';
import messages from 'containers/Clipboard/messages.js';
import { featureKeys } from 'services/FeatureFlagService';
import UndoRedoService from 'services/UndoRedoService';
import { getAction } from 'utils/undoredo';
import { EVENT_NAMES } from 'services/UndoRedoService/constants/undoRedo';

import { CDP } from 'pluginServices';
import { dispatch } from 'pluginServices/store';
import { clip } from 'pluginServices/selectionState/actionCreators';
import CoursewareTargetService from 'pluginServices/services/CoursewareTargetService';
import SelectionService from 'pluginServices/services/SelectionServices';
import { copyPasteElements } from './copyPasteElements';
import { cloneDeep } from 'lodash';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import { DEFAULT_SCREEN_VERSION } from 'builder/Pathway/components/ScreenEditor/Sidebar/Screens/constants';

export const CopyContainer = compose(enhanceWithFeatureFlagSubscription)(({
  assetManager,
  children,
  clipboard = [],
  componentService,
  editorState = {},
  interactiveService,
  selections = [],
  getFeatureFlagValue,
  activityService,
}) => {
  const copyRef = useRef(null);
  const isCopyPasteFixEnabled = getFeatureFlagValue(featureKeys.BRNT_17179_COPY_PASTE_INCONSISTENCY_FIX);

  const pasteHandler = async (event) => {
    // @NOTE: Workspace Paste - Interactives & Activities
    const clipping = getClip(event);
    if (!clipping) return; 
    const screenId = selections.find(({ type }) => type === 'INTERACTIVE')?.id;
    const pasteScreenId = JSON.parse(clipping)?.[0]?.id;
    const rootActivityId = await activityService.getRootActivityId(selections[0]?.id);
    const rootActivity = await activityService.getActivity(rootActivityId);
    const supportedScreenVersion = rootActivity.config.supportedScreenVersion || DEFAULT_SCREEN_VERSION;

    const fetchTitle = (copiedScreenId) => interactiveService.getInteractive(copiedScreenId);

    // @NOTE: Need to generate valid CoursewareTarget from
    // `screenId` in order to access `SelectionService`
    // methods from outside redux -> subscriber loop....
    // This is how screens/interactives are selected programmatically
    // in other areas of the Application. This will begin to make more
    // sense as the API gets implemented and applied more widely.
    //
    // @TODO: Currently logging potential errors because it's
    // unclear how these errors would occur and what those errors
    // would mean to a user (i.e. is this a case we need to ignore
    // like in the `copyHandler` or something we need to provide
    // visual feedback for).

    CoursewareTargetService
      .build()
      .createTarget(screenId)
      .then((target) => {
        SelectionService.build({ target }).paste(clipping);
        return fetchTitle(pasteScreenId);
      })
      .then((fetchedScreen) => {
        if(fetchedScreen === null) return;
        const fetchedTitle = fetchedScreen?.config?.title;
        const showVersionMixMatchError = fetchedScreen.plugin.version !== supportedScreenVersion;
        const message = intl.formatMessage( showVersionMixMatchError ? messages.screenVersionMixMatchError : messages.screenPasteSuccess, { title: fetchedTitle });
        dispatch(pushToast({ message }));
      })
      // eslint-disable-next-line no-console
      .catch(console.error);
  };

  const copyHandler = (event) => {
    // @NOTE: Workspace Copy - Interactives & Activities
    const hasTargetValue = Boolean(event?.target?.value); // Check event target for valid value
    const hasTextSelection = !(window.getSelection().isCollapsed); // Check Selection API for valid value
    const hasSpfSelection = Boolean(editorState.selectedPath);
    const hasCoursewareSelection = selections.length > 0;

    if (hasTargetValue || hasTextSelection) return; // ignore native copy events
    if (hasSpfSelection) return; // ignore stage elements
    if (hasCoursewareSelection) {
      interactiveService.getInteractive(selections[0]?.id).then((screen) => {
        const title = screen?.config?.title;
        setClip(selections, title);
      });
    }
  };

  /**
   * Create a component instance for an interactive
   * @func createComponent
   * @param {String} screenId - ID of parent interactive
   * @param {Object} el - component to carry
   * @param {String} el._pluginId - ID of template plugin
   * @param {String} el._pluginVersion - Version of template plugin
   * @param {Object} el._snapshot - snapshot of component state at time of copy
   *
   * @return {Object} - updated copy of component data with instance `_componentId`
    * */
  const createComponent = useCallback(async (screenId, el) => {
    const _componentId = await componentService.createInteractiveComponent(
      screenId,
      el._pluginId,
      el._pluginVersion,
      el._snapshot,
    );

    return { _componentId, ...el };
  }, [componentService]);

  /**
   * Register a given URN to a component instance.
   * @func addAssetToComponent
   * @param {String} componentId - component instance ID
   * @param {String} urn - asset URN
   * @return {void}
   *
   * (componentId: string) => (urn: string) => void
    * */
  const addAssetToComponent = useCallback((componentId) => (urn) => {
    assetManager.informAssetUsage(componentId, 'COMPONENT', urn);
  }, [assetManager]);

  /**
   * Register a given schema to a scope.
   * @func createSchemaCallback
   * @param {String} screenId - parent interactive of component instance
   * @param {String} componentId - component instance ID
   * @param {Object} schema - component instance schema
   * @return {Promise}
   *
   * (screenId: string, componentId: string) => (schema: Object) => Promise<RTMMessage|undefined>
    * */
  const createSchemaCallback = useCallback((screenId, componentId) => (schema) => {
    const learnerEditableProperties = findPropertiesByScope(schema);
    if (!learnerEditableProperties.length) return Promise.reject();
    return interactiveService.authorStudentScopeRegister(screenId, componentId);
  }, [interactiveService]);



  const convertToSelectSection = useCallback((path, isSectionCopied, interactiveConfig) => {
    if (editorState.isMultiSelect || isSectionCopied || (path == null && path !== undefined)) {
      return [interactiveConfig.stage.sections.length - 2];
    }

    if (path?.length === 1 && clipboard[0].stage.length > 1) {
      return [path[0] - 1];
    } else if (
      path?.length === 1 &&
      clipboard[0].stage.length === 1 &&
      path[0] !== clipboard[0].stage[0]._path[0]
    ) {
      return [path[0]].concat([0, 0, 0]);
    }

    /* Reselection needs to be refactored as we are dealing with both section, individual and multiple element pasting */
    return path;
  }, [editorState, clipboard]);

  /**
   * Duplicate a given stage model element
   * @func duplicate
   * @param {Object} data - clipboard data
   * @param {Object} data.screen - selection API data (i.e. current interactive)
   * @param {Object} data.stage - stage element data pulled from clipboard
   * @param {Object} data.stage.element - stage element value
   * @param {Object} [data.stage.element.pluginId] - plugin ID if stage element is component
   * @param {Object} [data.stage.element.pluginVersion] - plugin version number if stage element is component
   * @return {void}
   *
   * async (data: Object) => void
    * */
  const duplicate = useCallback(async (data) => {
    if (!data) return;

    const { screen, stage, isMoveTo = false } = data;
    const isSectionCopied = stage[0]?.type === STAGE_ELEMENT_TYPES.SECTION;

    // @NOTE: Get and set data outside of this function so that
    // we can encapsulate stage computations and updates inside
    // of a reducer that operates on a stream or list of elements
    // (i.e. section or multi-select) and limit the number network
    // side-effects to the "edges".
    //
    // You can think of each discreet duplication cycle as "mapping"
    // an interactive's stage model from one state to another (i.e.
    // State -> State), and we're just combining all those maps into
    // one reducer function (i.e. "mapReduce").
    const { config: currentInteractiveConfig } = await interactiveService.getInteractive(screen.id);

    let action;
    let actionData = [];
    let currentInteractiveComponentIds = [];
    const schemaCallbacks = [];
    const assetLists = [];
    const registerComponentEffects = (el) => {
      if (el.type === STAGE_ELEMENT_TYPES.PLUGIN) {
        schemaCallbacks.push([el._pluginId, el._pluginVersion, createSchemaCallback(screen.id, el._componentId)]);
        assetLists.push({ componentId: el._componentId, urns: extractUrnFromString(JSON.stringify(el._snapshot)) });
      }
    };

    let sectionIndex = isSectionCopied ? currentInteractiveConfig.stage.sections.length : editorState.selectedPath?.[0] || 0;
    const inferElementContext = (el) => ({ ...el, _belongsToSection: stage.length > 1 || isMoveTo });

    const getPath = (el, sectionIndex) => {
      if (el._belongsToSection) {
        return el._path.length === 4 ?
          [sectionIndex].concat(el._path.slice(1, 3)).concat([el._path[3] + 1]) :
          [sectionIndex].concat(el._path.slice(1));
      }
      if (editorState?.selectedPath?.length === 1) {
        return [sectionIndex, 0, 0, 0];
      }
      return editorState?.selectedPath || el._path;
    };

    const computeTargetPath = (el) => {
      if (!el._belongsToSection) {
        return ({ ...el, _path: getPath(el, sectionIndex) });
      }

      if (isMoveTo) {
        return ({ ...el, _path: getPath(el, currentInteractiveConfig.stage.sections.length) });
      }

      if (editorState.isMultiSelect) {
        return ({ ...el, _path: getPath(el, currentInteractiveConfig.stage.sections.length) });
      }

      return ({ ...el, _path: getPath(el, sectionIndex) });
    };

    let _computedConfig = currentInteractiveConfig;
    let targetSelectedPath = [];
    let targetSelectedElement = {};

    if(isCopyPasteFixEnabled) {
      const data = await copyPasteElements(cloneDeep(stage),cloneDeep(currentInteractiveConfig), createComponent, screen.id, editorState, registerComponentEffects);
      _computedConfig = data.config;
      targetSelectedPath = data.targetSelectedPath;
      targetSelectedElement = data.targetSelectedElement;
    } else {
      _computedConfig = await Promise.all(
        stage.map((el) => isNotPlugin(el)
          ? Promise.resolve(el)
          : createComponent(screen.id, el)
        )
      ).then(
        (stageElements) => stageElements
          .map(pipe(
            inferElementContext,
            computeTargetPath,
            performSideEffectWithValueAndReturnOriginal(registerComponentEffects),
            registerNewElement,
            generateNewDataIds,
            (element) => identifyAndRegisterImagesOfElement(element, screen.id),
            serializeOrUpdateRefsInPluginConfigs,
          ))
          .reduce(reduceInteractiveConfig, currentInteractiveConfig)
      );
    }

    // Save new interactive stage
    await interactiveService.saveInteractiveConfig(screen.id, _computedConfig);

    if (currentInteractiveConfig.components) {
      currentInteractiveComponentIds = currentInteractiveConfig.components.map((component) => component.itemId);
    }

    if (_computedConfig.components && currentInteractiveComponentIds.length) {
      actionData = _computedConfig.components.reduce((acc, component) => {
        if (!currentInteractiveComponentIds.includes(component.itemId)) {
          acc.push(component);
        }
        return acc;
      }, []);
    }

    // Compute new Editor state
    let newEditorState = null;

    const targetPath = isCopyPasteFixEnabled ? targetSelectedPath : convertToSelectSection(editorState.selectedPath, isSectionCopied, _computedConfig);
    const targetType = isCopyPasteFixEnabled ? targetSelectedElement.type : stage[0].type;

    let targetComponentId;
    
    // capturing the componentId, if the selection target type is a plugin
    if(!editorState.isMultiSelect && (stage[0].type === STAGE_ELEMENT_TYPES.PLUGIN || targetSelectedElement?.type === STAGE_ELEMENT_TYPES.PLUGIN)) {
      if(isCopyPasteFixEnabled) {
        const targetComponent = _computedConfig.components?.find((component) => component.localRef === targetSelectedElement.ref || component.localRef === targetSelectedElement.localRef);
        targetComponentId = targetComponent?.itemId;
      } else {
        const targetComponent = _computedConfig.components?.find((component) => component.localRef === stage[0].ref || component.localRef === stage[0].localRef);
        targetComponentId = targetComponent?.itemId;
      }
    }

    if(editorState?.selectedPath) {
      newEditorState = computeEditorSelection({
        screenId: screen.id,
        selectedPath: targetPath,
        elementType: editorState.isMultiSelect ? STAGE_ELEMENT_TYPES.SECTION : targetType,
        ...(targetComponentId && { componentId: targetComponentId }),
        stageRef: editorState.stageRef,
      });
    }

    if (actionData.length) {
      action = getAction(EVENT_NAMES.PASTE_COMPONENTS, { data: actionData, editorState: newEditorState });
    } else {
      action = getAction(EVENT_NAMES.PASTE_ELEMENTS, { editorState: newEditorState });
    }

    UndoRedoService.recordAction(action, _computedConfig[STAGE], screen.id);

    // TODO: Component specific side-effects can be executed asynchronously
    // without creating or relying on any tightly coupled dependencies.
    if (schemaCallbacks.length) {
      schemaCallbacks.forEach((actionArgs) => {
        dispatch(getPluginSchema(...actionArgs));
      });
    }

    if (assetLists.length) {
      assetLists.forEach(({ componentId, urns }) => {
        urns.forEach(addAssetToComponent(componentId));
      });
    }

    // Save new Editor state
    if(newEditorState) CDP.editor().select(newEditorState);
  }, [interactiveService, editorState, isCopyPasteFixEnabled, createSchemaCallback, createComponent, convertToSelectSection, addAssetToComponent]);

  useEffect(() => {
    if (!clipboard.length) return;
    const paste = (data) => { data.forEach(duplicate); };
    paste(clipboard);
    dispatch(clip([]));
  }, [clipboard, duplicate]);

  useEffect(() => {
    copyRef.current.focus();
  }, []);



  return (
    <div
      tabIndex="-1"
      ref={copyRef}
      onCopy={copyHandler}
      onPaste={pasteHandler}
      className="copy-provider"
      style={{height: '100%'}}
      data-undo-redo = {EVENT_NAMES.STAGE_UNDO_REDO}
    >{children}</div>
  );
}, [featureKeys.BRNT_17179_COPY_PASTE_INCONSISTENCY_FIX]);

CopyContainer.propTypes = {
  assetManager: PropTypes.shape({
    informAssetUsage: PropTypes.func,
  }),
  children: PropTypes.element,
  clipboard: PropTypes.array,
  componentService: PropTypes.shape({
    createInteractiveComponent: PropTypes.func,
  }),
  editorState: PropTypes.object,
  interactiveService: PropTypes.shape({
    authorStudentScopeRegister: PropTypes.func,
    getInteractive: PropTypes.func,
    saveIntearctive: PropTypes.func,
  }),
  selections: PropTypes.array,
  activityService: PropTypes.shape({
    getRootActivityId: PropTypes.func,
  }),
};